import type { CurveProfile } from "./curve";
import type { Session } from "./effective-time";

export type { Session };

export type MeterId = "food" | "rest" | "fun" | "clean" | "love";
export const METER_IDS: readonly MeterId[] = ["food", "rest", "fun", "clean", "love"];

/**
 * A care action refills its meter to 100%. `at` is milliseconds since the Unix epoch. Without `fill` the refill is
 * instant (every save made before gradual refills). With `fill` (milliseconds, at most 10 minutes) the meter climbs
 * in a straight line from its level at `at` to 100% and its decay clock starts when the climb ends (docs/SPEC.md section 3).
 */
export interface CareEvent {
  at: number;
  meter: MeterId;
  fill?: number;
  /**
   * The pet woke up before a sleeping refill was over: the meter stops climbing and keeps the level it had reached, and
   * decays from there. Without a refill in progress it does nothing. Never counts as care.
   */
  stop?: boolean;
}

/**
 * Everything the simulation needs about one pet. The state of the pet at any time is a pure function of this log and
 * the evaluation time, so a server can replay the same log and get the same answer.
 */
export interface PetLog {
  /** The animal, so a server can check the curves belong to a real animal. */
  species: string;
  /** The first app open of the week. */
  hatchedAt: number;
  /** The week this pet belongs to, for example "2026-W43". */
  weekId: string;
  /** The IANA time zone locked at hatch. */
  timeZone: string;
  /** Each animal has its own curve for each meter (docs/SPEC.md section 3). */
  curves: Record<MeterId, Partial<CurveProfile>>;
  /** How long each care action takes to refill its meter, in milliseconds, frozen at hatch like the curves. Older pets have none. */
  refills?: Partial<Record<MeterId, number>>;
  care: CareEvent[];
  sessions: Session[];
}
